import { CSSProperties, useEffect, useState } from 'react';
import useTranslation from '../../customHooks/useTranslation';
import LoopFigure from './LoopFigure';
import BookingPanel from './BookingPanel';
import RevisionCloud from './RevisionCloud';

const CYCLE_MS = 3600;

export const words = (text: string) => text.split(' ').length;
export const delay = (seconds: number) => ({ ['--delay' as string]: `${seconds}s` }) as CSSProperties;

// The heading is lettered word by word; the key restarts the sequence when the language changes.
export function Lettered({ text }: { text: string }) {
	return (
		<span key={text}>
			{text.split(' ').map((word, i, all) => (
				<span key={i}>
					<span className='lettered-word' style={{ ['--i' as string]: i } as CSSProperties}>
						{word}
					</span>
					{i < all.length - 1 && ' '}
				</span>
			))}
		</span>
	);
}

function usePrefersReducedMotion() {
	const [reduced, setReduced] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
	useEffect(() => {
		const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
		const onChange = () => setReduced(mq.matches);
		mq.addEventListener('change', onChange);
		return () => mq.removeEventListener('change', onChange);
	}, []);
	return reduced;
}

// The promise names the span of the work, so that span is dimensioned like a drawing:
// the [bracketed] phrase gets a redline dimension line with architectural ticks.
function Statement({ text, start }: { text: string; start: number }) {
	const [before, measured = '', after = ''] = text.split(/\[|\]/);
	return (
		<p className='rise-in type-wide mt-6 max-w-[30ch] text-[clamp(1.2rem,1.6vw,1.4rem)] font-semibold leading-[1.6] tracking-[-0.015em] text-ink' style={delay(start)}>
			{before}
			{measured && (
				<span className='relative inline-block whitespace-nowrap'>
					{measured}
					<span aria-hidden='true' className='absolute inset-x-0 -bottom-[5px] h-[13px]'>
						<span className='draw-x absolute inset-x-0 top-[6px] h-px bg-redline' style={delay(start + 0.25)} />
						<span className='rise-in absolute left-0 top-0 h-[13px] w-px -translate-x-1/2 rotate-45 bg-redline' style={delay(start + 0.2)} />
						<span className='rise-in absolute right-0 top-0 h-[13px] w-px translate-x-1/2 rotate-45 bg-redline' style={delay(start + 0.75)} />
					</span>
				</span>
			)}
			{after}
		</p>
	);
}

export default function Hero() {
	const t = useTranslation();
	const reduced = usePrefersReducedMotion();
	const [active, setActive] = useState(1);
	const [paused, setPaused] = useState(false);
	const [hovering, setHovering] = useState(false);
	const running = !paused && !hovering && !reduced;

	useEffect(() => {
		if (!running) return;
		const id = window.setInterval(() => setActive(a => (a % 6) + 1), CYCLE_MS);
		return () => window.clearInterval(id);
	}, [running]);

	const phase = t.hero.phases[active - 1];

	return (
		<section id='top' aria-labelledby='hero-heading' className='drafting-grid relative'>
			<div className='mx-auto grid max-w-[1320px] gap-x-12 gap-y-14 px-4 pb-16 pt-10 sm:px-8 lg:grid-cols-12 lg:pb-20 lg:pt-14'>
				<div className='lg:col-span-6'>
					<h1 id='hero-heading' className='lettering-guide type-wide max-w-[17ch] text-[clamp(2.4rem,4.6vw,4.4rem)] font-bold leading-[0.98] tracking-[-0.035em] text-ink'>
						<Lettered text={t.hero.heading} />
					</h1>
					<p className='rise-in mt-6 flex flex-wrap items-baseline gap-x-3 text-lg text-ink' style={delay(0.55 + words(t.hero.heading) * 0.07)}>
						<span className='font-semibold'>Kamil Pfaff</span>
						<span aria-hidden='true' className='draw-x h-px w-6 translate-y-[-0.3em] bg-redline' style={delay(0.75 + words(t.hero.heading) * 0.07)} />
						<span className='text-ink-soft'>{t.hero.role}</span>
					</p>
					<p className='rise-in mt-4 max-w-[52ch] text-[1.06rem] leading-relaxed text-ink-soft' style={delay(0.68 + words(t.hero.heading) * 0.07)}>
						{t.hero.lede}
					</p>
					<Statement text={t.hero.promise} start={0.8 + words(t.hero.heading) * 0.07} />

					<RevisionCloud tag={t.hero.revision} className='mx-3 mt-[4.5rem] max-w-[520px] sm:mx-0'>
						<BookingPanel />
					</RevisionCloud>
				</div>

				<figure className='flex flex-col lg:col-span-6' onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)} onFocus={() => setHovering(true)} onBlur={() => setHovering(false)}>
					<LoopFigure active={active} labels={t.hero.phases.map(p => p.name)} span={t.hero.figure.span} onSelect={setActive} className='w-full lg:mt-6' />

					<ol className='mt-4 grid grid-cols-2 gap-x-4 gap-y-2 sm:hidden'>
						{t.hero.phases.map((p, i) => (
							<li key={p.name}>
								<button type='button' onClick={() => setActive(i + 1)} aria-pressed={active === i + 1} className={`flex w-full items-center gap-2 text-left text-sm ${active === i + 1 ? 'text-redline' : 'text-ink-soft'}`}>
									<span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-bold ${active === i + 1 ? 'border-redline bg-redline text-sheet-deep' : 'border-ink-soft text-ink'}`}>{i + 1}</span>
									{p.name}
								</button>
							</li>
						))}
					</ol>

					<figcaption className='mt-6 grid grid-cols-[auto_1fr_auto] items-start gap-x-4 border-t border-ink-line pt-4'>
						<span className='lettering pt-0.5 text-label text-ink-faint'>
							{t.hero.figure.caption}
							<span className='mt-1 block normal-case tracking-normal text-ink-soft'>{t.hero.figure.title}</span>
						</span>
						<span className='min-h-14 border-l border-ink-line pl-4'>
							<span className='lettering block text-label text-redline'>
								{active} · {phase.name}
							</span>
							<span className='mt-1 block text-[1.05rem] text-ink'>{phase.line}</span>
						</span>
						{!reduced && (
							<button type='button' onClick={() => setPaused(p => !p)} className='flex h-9 w-9 items-center justify-center border border-ink-line text-ink-soft transition-colors hover:border-ink-soft hover:text-ink' aria-label={paused ? t.hero.figure.play : t.hero.figure.pause} aria-pressed={paused}>
								<svg viewBox='0 0 16 16' className='h-3.5 w-3.5' aria-hidden='true'>
									{paused ? <path d='M4 2.5v11l9-5.5z' fill='currentColor' /> : <path d='M4 2.5h2.6v11H4zM9.4 2.5H12v11H9.4z' fill='currentColor' />}
								</svg>
							</button>
						)}
					</figcaption>

					<TitleBlock />
				</figure>
			</div>
		</section>
	);
}

function TitleBlock() {
	const t = useTranslation();
	const tb = t.hero.titleBlock;
	const cells = [
		{ label: tb.project, value: tb.projectValue, wide: true },
		{ label: tb.architect, value: 'Kamil Pfaff' },
		{ label: tb.credentials, value: 'RHCSA · RHCE' },
		{ label: tb.sheet, value: 'A-001' },
		{ label: tb.scale, value: '1:1' },
	];
	return (
		<dl className='mt-10 grid grid-cols-2 border-l border-t border-ink-line sm:grid-cols-[1.6fr_1.2fr_1.2fr_0.7fr_0.6fr] lg:ml-auto lg:w-[min(100%,640px)]'>
			{cells.map(c => (
				<div key={c.label} className={`border-b border-r border-ink-line px-3 py-2 ${c.wide ? 'col-span-2 sm:col-span-1' : ''}`}>
					<dt className='lettering text-[0.62rem] text-ink-faint'>{c.label}</dt>
					<dd className='mt-0.5 truncate text-sm font-semibold text-ink'>{c.value}</dd>
				</div>
			))}
		</dl>
	);
}
